/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Form, Grid } from '@mairistem/composants';
import { AdresseForm } from '@mairistem/adresses';

import { SectionHeader } from '@/components/Common';
import { useAdresseFormFiche } from '@/hooks';

import PanelModalEdition from '../../PanelModalEdition';
import CommuneDelegueField from '../CommuneDelegueField';

interface InformationsDeclarationAttestationsModalProps {
  data: any;
  onSave: () => Promise<void>;
  onChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
  onCancel: () => void;
}

const InformationsDeclarationAttestationsModal = (
  props: InformationsDeclarationAttestationsModalProps,
) => {
  const { data, onSave, onChange, onClose, onCancel } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Record<string, string | boolean> & { api?: boolean }
  >({});

  const handleAdresseChange = React.useCallback(
    (adresse: Record<string, unknown>) =>
      onChange({ adresseCommune: { ...data?.adresseCommune, ...adresse } }),
    [onChange, data?.adresseCommune],
  );
  const adresseForm = useAdresseFormFiche(
    data?.adresseCommune,
    handleAdresseChange,
  );

  const handleChangeCommuneDelegue = React.useCallback(
    (libelle: string, drapeau: boolean) => {
      onChange({
        adresseCommune: {
          ...data?.adresseCommune,
          communeDelegue: libelle,
          estCommuneDelegue: drapeau,
        },
      });
    },
    [onChange, data?.adresseCommune],
  );

  const handleSave = React.useCallback(() => {
    setLoading(true);

    onSave()
      .then(() => {
        onClose();
      })
      .catch(() => {
        setErrors({ api: true });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [onClose, onSave]);

  return (
    <PanelModalEdition
      data-testid="attestations-modal"
      errors={errors}
      loading={loading}
      header="ATTESTATIONS"
      onSave={handleSave}
      onClose={onCancel}
    >
      <Form>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <SectionHeader title="ATTESTATION" style={{ marginTop: 0 }} />
              <Form.Checkbox
                style={{ marginLeft: '1em' }}
                label="Attestation de lien de parenté"
                data-testid="declaration-lien-parente"
                checked={data?.attestationLienParente === 1}
                onChange={(_e, target) =>
                  onChange({ attestationLienParente: target?.checked ? 1 : 0 })
                }
              />
              <SectionHeader
                title="RÉSIDENCE COMMUNE"
                style={{ marginTop: '1.5rem' }}
              />
              <div style={{ margin: '1em' }}>
                <Form.Checkbox
                  label="Établir la résidence commune à l'adresse suivante :"
                  data-testid="declaration-attestation-adresse-commune"
                  checked={data?.attestationAdresseCommune === 1}
                  onChange={(_e, target) =>
                    onChange({
                      attestationAdresseCommune: target?.checked ? 1 : 0,
                    })
                  }
                />
                <AdresseForm
                  saisie="libre"
                  handleTypes
                  displayDepartement={false}
                  displayArticle
                  disabled={data?.attestationAdresseCommune !== 1}
                  adresse={adresseForm.adresseFormValue}
                  onItemChange={adresseForm.onItemChange}
                  onChange={adresseForm.onChange}
                />
                {/* Même espacement (2em) qu'entre les lignes d'AdresseForm */}
                <div style={{ marginTop: '2em' }}>
                  <CommuneDelegueField
                    data-testid="declaration-commune-delegue"
                    libelle={data?.adresseCommune?.communeDelegue}
                    drapeau={data?.adresseCommune?.estCommuneDelegue}
                    disabled={data?.attestationAdresseCommune !== 1}
                    onChange={handleChangeCommuneDelegue}
                  />
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </PanelModalEdition>
  );
};

export default InformationsDeclarationAttestationsModal;
